import { useState, useRef, useEffect, useCallback } from 'react'
import { post } from '../lib/api'
import type { Proposal } from '../lib/agent'

/**
 * Voice of God — the admin command line.
 *
 * One input, plain instructions, and a junior developer on the other end who
 * does the safe obvious things and escalates the rest. It is always the last
 * thing on the screen and always focusable with `/`, because the whole point is
 * that acting on what you just read costs one keystroke rather than a trip to
 * a form.
 *
 * The reply is deliberately verbose about what it DID: "set to Complete, the
 * workbook still says Not Started" is the sentence that stops an override
 * quietly becoming a fact.
 */

type HelpDoc = { title: string; groups: Array<{ name: string; lines: string[][] }>; ids: string; truth: string }
type Reply = {
  ok: boolean; outcome: 'ok' | 'refused' | 'escalated' | 'error'; message: string
  ticketId?: string; lastRef?: string | null
  detail?: { help?: HelpDoc; entity?: string; ref?: string; row?: Record<string, unknown>
    overrides?: Array<{ field: string; value: string }>; notes?: Array<{ body: string; created_at: number }> }
  /** Set when Ollama turned prose into the command that actually ran. */
  translatedFrom?: string
}

const FIELD_LABEL: Record<string, string> = {
  task: 'Task', workstream: 'Workstream', owner: 'Owner', due_on: 'Due', status: 'Status',
  dependency: 'Depends on', evidence: 'Evidence', next_action: 'Next action', risk_if_late: 'Risk if late',
  name: 'Gate', pass_condition: 'Pass condition', if_not_passed: 'If not passed',
  risk: 'Risk', impact: 'Impact', likelihood: 'Likelihood', mitigation: 'Mitigation',
  where_to_fix: 'Where to fix', note: 'Note',
}

function RowAnswer({ detail }: { detail: NonNullable<Reply['detail']> }) {
  const row = detail.row ?? {}
  const overridden = new Set((detail.overrides ?? []).map((o) => o.field))
  const entries = Object.entries(row).filter(([k, v]) => k !== 'ref' && k !== 'sort' && v !== null && v !== '')
  return (
    <div className="vog-answer">
      <div className="vog-answer-head">{detail.ref}</div>
      <dl>
        {entries.map(([k, v]) => (
          <div key={k}>
            <dt>{FIELD_LABEL[k] ?? k}</dt>
            <dd>
              {String(v)}
              {overridden.has(k) && <span className="cc-ovr">set here</span>}
            </dd>
          </div>
        ))}
      </dl>
      {(detail.notes ?? []).length > 0 && (
        <div className="vog-notes">
          <strong>Notes</strong>
          {(detail.notes ?? []).map((n, i) => (
            <p key={i}>{n.body} <span className="cc-sub">{new Date(n.created_at).toLocaleDateString()}</span></p>
          ))}
        </div>
      )}
    </div>
  )
}

function HelpPanel({ help }: { help: HelpDoc }) {
  return (
    <div className="vog-help">
      {help.groups.map((g) => (
        <div key={g.name} className="vog-help-group">
          <strong>{g.name}</strong>
          <dl>
            {g.lines.map(([cmd, what]) => (
              <div key={cmd}><dt><code>{cmd}</code></dt><dd>{what}</dd></div>
            ))}
          </dl>
        </div>
      ))}
      <p className="cc-sub">{help.ids}</p>
      <p className="cc-sub">{help.truth}</p>
    </div>
  )
}

export default function VoiceOfGod({ onChanged }: { onChanged: () => void }) {
  const [command, setCommand] = useState('')
  const [busy, setBusy] = useState(false)
  const [replies, setReplies] = useState<Array<Reply & { raw: string }>>([])
  // The row the conversation is currently about, so "it done" works after
  // "tell me about C02". Held on the client: a server-side conversation would
  // be one more thing to expire and get out of step with what is on screen.
  const [lastRef, setLastRef] = useState<string | null>(null)
  // Whether the interpreter answered last time. Not probed on mount: it lives
  // on the server now, so the only honest test is asking it something.
  const [llm, setLlm] = useState<{ up: boolean; tried: boolean }>({ up: true, tried: false })
  const [thinking, setThinking] = useState(false)
  // What the agent proposes to do, waiting to be confirmed. Reads never land
  // here — only the things that write.
  const [pending, setPending] = useState<Proposal | null>(null)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // "/" focuses the command line from anywhere, unless you are already typing.
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
      if (e.key === '/' && !typing) {
        e.preventDefault()
        input.current?.focus()
      }
    }
    /*
     * Ask The Ridge, in the masthead, fires this rather than opening a dialog.
     * The command line stays on the page — a modal would mean reading the
     * screen and acting on it were two different journeys again — so the
     * button scrolls to the input and focuses it.
     */
    const onAsk = () => {
      input.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      input.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('ridge:ask', onAsk)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('ridge:ask', onAsk)
    }
  }, [])

  const execute = useCallback(async (raw: string, translatedFrom?: string) => {
    const reply = await post<Reply>('/api/voice', { command: raw, lastRef })
    setReplies((r) => [{ ...reply, raw, translatedFrom }, ...r].slice(0, 6))
    if (reply.lastRef !== undefined) setLastRef(reply.lastRef)
    // Refresh whatever the command changed, so the screen never disagrees with
    // the thing it just told you it did.
    onChanged()
  }, [lastRef, onChanged])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const raw = command.trim()
    if (!raw || busy) return
    setBusy(true)
    try {
      /*
       * Ask the grammar first, without writing anything.
       *
       * A dry run is what makes the model optional: if the sentence already
       * parses, nothing is translated and nothing is slower. Only the input
       * that would have become a ticket goes to Ollama.
       */
      // Does the grammar already understand it? Nothing to interpret if so.
      const dry = await post<Reply>('/api/voice', { command: raw, lastRef, dryRun: true })
      if (dry.ok) {
        await execute(raw)
        setCommand('')
        return
      }

      /*
       * Prose. Ask the interpreter what it thinks this is.
       *
       * A read happens at once — showing a row changes nothing, and confirming
       * a question would be theatre. Anything that WRITES is proposed and
       * waits, so the operator sees the row it chose before it happens.
       */
      setThinking(true)
      let proposal: Proposal | null = null
      let available = false
      let answered: string | null = null
      try {
        const out = await post<{ proposal: Proposal | null; available: boolean; answer?: string | null }>(
          '/api/voice/interpret', { command: raw },
        )
        proposal = out.proposal
        available = out.available
        answered = out.answer ?? null
      } catch {
        available = false
      }
      setThinking(false)
      setLlm({ up: available, tried: true })

      /*
       * A question gets an answer, not a row.
       *
       * Every read used to end in show_row, so "where do I send these credit
       * reports?" printed a task and answered nothing. Routing is not replying.
       */
      if (answered) {
        const said: Reply & { raw: string } = { ok: true, outcome: 'ok', message: answered, raw }
        setReplies((r) => [said, ...r].slice(0, 6))
        setCommand('')
        return
      }

      if (!proposal) {
        /*
         * Say so, and do NOT quietly file a ticket.
         *
         * The old path ran the raw sentence when there was no interpreter,
         * which produced a grammar error AND a ticket for every question asked
         * — twice reported, both times looking like a broken parser rather than
         * an interpreter that had never been reachable.
         */
        const why = available
          ? 'I could not work out which row you meant.'
          : 'The interpreter did not answer, so only exact commands work right now.'
        const refusal: Reply & { raw: string } = {
          ok: false, outcome: 'refused', raw,
          message: `${why} Type help, or start with a row id like C01.`,
        }
        setReplies((r) => [refusal, ...r].slice(0, 6))
        return
      }

      // Checked against the grammar before it is offered, so an invented row id
      // never reaches a confirm card.
      const check = await post<Reply>('/api/voice', { command: proposal.command, lastRef, dryRun: true })
      if (!check.ok) {
        const bad: Reply & { raw: string } = {
          ok: false, outcome: 'refused', raw,
          message: `It suggested "${proposal.command}", which is not valid. Nothing was changed.`,
        }
        setReplies((r) => [bad, ...r].slice(0, 6))
        return
      }

      if (proposal.immediate) {
        await execute(proposal.command, raw)
        setCommand('')
      } else {
        setPending(proposal)
      }
    } catch (err) {
      const failure: Reply & { raw: string } = {
        ok: false, outcome: 'error', message: err instanceof Error ? err.message : 'Failed.', raw,
      }
      setReplies((r) => [failure, ...r].slice(0, 6))
    } finally {
      setBusy(false)
      setThinking(false)
    }
  }

  return (
    <section className="vog" aria-label="Voice of God">
      <div className="vog-head">
        <h2>Voice of God</h2>
        <span className="muted vog-hint">
          press <kbd>/</kbd> to focus · type <button type="button" className="link" onClick={() => { setCommand('help'); input.current?.focus() }}>help</button>
          {lastRef && <> · talking about <strong>{lastRef}</strong></>}
          {llm.tried && !llm.up && <> · <span className="vog-down">interpreter unavailable</span></>}
        </span>
      </div>

      <form onSubmit={send} className="vog-form">
        <input
          ref={input}
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder={lastRef ? `ask about ${lastRef}, or "it done" · type help` : 'tell me about C01 · C01 done · help'}
          aria-label="Command"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
        />
        <button type="submit" disabled={busy || !command.trim()}>{thinking ? 'reading…' : busy ? '…' : 'Do it'}</button>
      </form>

      {pending && (
        <div className="vog-confirm" role="alertdialog" aria-label="Confirm this change">
          <div className="vog-confirm-what">
            <strong>{pending.says}</strong>
            <code>{pending.command}</code>
          </div>
          <div className="vog-confirm-do">
            <button
              type="button"
              onClick={async () => {
                const p = pending
                setPending(null)
                setBusy(true)
                try { await execute(p.command, command.trim()); setCommand('') } finally { setBusy(false) }
              }}
            >
              Do it
            </button>
            <button type="button" className="link" onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}


      {replies.length > 0 && (
        <ul className="vog-log">
          {replies.map((r, i) => (
            <li key={i} className={`vog-reply vog-${r.outcome}`}>
              {r.translatedFrom && <span className="vog-from">you said: {r.translatedFrom}</span>}
              <code>{r.raw}</code>
              <span>{r.message}</span>
              {r.detail?.help && <HelpPanel help={r.detail.help} />}
              {r.detail?.row && <RowAnswer detail={r.detail} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
